import React, { useState } from 'react';
import { FinancialInstitution, CreateFinancialInstitution } from '@/types/financialInstitution';
import financialInstitutionApiService from '@/services/financialInstitutionApi'; // Pour l'API réelle
import cloudinaryService from '@/services/cloudinaryService'; // Pour les uploads Cloudinary
import { useToast } from '@/hooks/useToast';
import { useUser } from '@/hooks/useUser';
import { cacheService } from '@/services/cacheService';

/**
 * ✨ PAS DE CONVERSION NÉCESSAIRE !
 * FinancialInstitutionResponse = CreateFinancialInstitutionRequest + id/dates
 * Le backend renvoie TOUTES les données du formulaire sans perte
 * On peut utiliser les données directement
 */

/**
 * Vérifie si une chaîne est un UUID valide (v4)
 * Les IDs mock comme "inst-123456" ne sont pas des UUIDs valides
 */
const isValidUUID = (id: string | undefined): boolean => {
    if (!id) return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
};

interface UseFinancialInstitutionOptions {
  enabled?: boolean;
  preferMock?: boolean;
}

export const useFinancialInstitution = (
    institutionId?: string, 
    options: UseFinancialInstitutionOptions = { enabled: true, preferMock: false }
) => {
    const [institution, setInstitution] = useState<FinancialInstitution | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [usingMockData, setUsingMockData] = useState<boolean>(options.preferMock || false);
    const [dataSource, setDataSource] = useState<'cache' | 'network' | 'none'>('none');
    const [isRevalidating, setIsRevalidating] = useState<boolean>(false);
    
    // ✨ SIMPLIFICATION: Refs pour tracking
    const loadingIdRef = React.useRef<string | null>(null);
    const lastLoadedIdRef = React.useRef<string | null>(null);
    const lastBackendFetchRef = React.useRef<number>(0);
    
    const { showToast } = useToast();
    const { user, updateProfile } = useUser();
    
    const userId = user?.id;
    const userFinancialInstitutionId = user?.financialInstitutionId;

    // ✨ CORRECTION: Utiliser l'ID effectif (props OU profil utilisateur)
    const effectiveInstitutionId = institutionId || userFinancialInstitutionId;
    
    // Vérifier si l'ID effectif est un UUID valide
    const hasValidUUID = isValidUUID(effectiveInstitutionId);

    // ============================================
    // 📦 CHARGEMENT DEPUIS INDEXEDDB (CACHE-FIRST)
    // ============================================
    
    const loadFromIndexedDB = React.useCallback(async (instId: string): Promise<FinancialInstitution | null> => {
        try {
            const cached = await cacheService.get<FinancialInstitution>('institution', instId);
            if (cached) {
                console.log('📦 [Institution] Chargé depuis IndexedDB:', cached.data?.denominationSociale);
                return cached.data;
            }
        } catch (err) {
            console.warn('⚠️ [Institution] Erreur lecture IndexedDB:', err);
        }
        return null;
    }, []);

    // ============================================
    // 🔄 REVALIDATION BACKGROUND (SYNC BACKEND)
    // ============================================
    
    const revalidateFromBackend = React.useCallback(async (instId: string): Promise<FinancialInstitution | null> => {
        // Anti-rebond: minimum 5 secondes entre les appels backend
        const now = Date.now();
        if (now - lastBackendFetchRef.current < 5000) {
            console.log('⏳ [Institution] Revalidation ignorée (trop rapprochée)');
            return null;
        }
        lastBackendFetchRef.current = now;

        try {
            console.log('🔄 [Institution] Revalidation depuis le backend...');
            setIsRevalidating(true);
            
            const backendData = await financialInstitutionApiService.getInstitution(instId);
            
            if (backendData) {
                // Vérifier si les données ont changé
                const cached = await cacheService.get<FinancialInstitution>('institution', instId);
                const hasChanges = !cached || cacheService.hasDataChanged(cached, backendData);
                
                if (hasChanges) {
                    console.log('✨ [Institution] Nouvelles données détectées, mise à jour du cache...');
                    // Mettre à jour IndexedDB
                    if (userId) {
                        await cacheService.set('institution', instId, backendData, userId, 'synced');
                    }
                    return backendData as FinancialInstitution;
                } else {
                    console.log('✅ [Institution] Données identiques, pas de mise à jour nécessaire');
                    // Juste mettre à jour le timestamp
                    await cacheService.updateSyncStatus('institution', instId, 'synced');
                }
            }
            return null;
        } catch (err) {
            console.error('❌ [Institution] Erreur revalidation backend:', err);
            throw err;
        } finally {
            setIsRevalidating(false);
        }
    }, [userId]);

    // ✨ OPTIMISATION: Fonction de chargement avec stratégie Cache-First
    const loadInstitution = React.useCallback(async (instId: string) => {
        // Éviter les appels redondants
        if (loadingIdRef.current === instId || lastLoadedIdRef.current === instId) {
            console.log('⏸️ Chargement déjà en cours ou terminé pour:', instId);
            return;
        }
        
        loadingIdRef.current = instId;
        setLoading(true);
        setError(null);
        
        try {
            // ============================================
            // 1️⃣ ÉTAPE 1: Charger depuis IndexedDB (instantané)
            // ============================================
            const cachedInstitution = await loadFromIndexedDB(instId);
            
            if (cachedInstitution) {
                console.log('✅ [Institution] Données affichées depuis le cache');
                setInstitution(cachedInstitution);
                setDataSource('cache');
                lastLoadedIdRef.current = instId;
                setLoading(false); // Arrêter le loading - les données sont affichées
                
                // ============================================
                // 2️⃣ ÉTAPE 2: Revalider en background (silencieux)
                // ============================================
                const cached = await cacheService.get<FinancialInstitution>('institution', instId);
                if (cached && !cacheService.isCacheValid(cached.metadata)) {
                    console.log('🔄 [Institution] Cache expiré, revalidation en background...');
                    revalidateFromBackend(instId).then(updatedData => {
                        if (updatedData) {
                            setInstitution(updatedData);
                            setDataSource('network');
                            setUsingMockData(false);
                        }
                    }).catch(() => {
                        console.warn('⚠️ [Institution] Revalidation échouée, données du cache utilisées');
                    });
                } else {
                    console.log('✅ [Institution] Cache encore valide, pas de revalidation nécessaire');
                }
                return;
            }
            
            // ============================================
            // 3️⃣ PAS DE CACHE: Charger directement depuis le backend
            // ============================================
            console.log('🌐 [Institution] Pas de cache, chargement depuis le backend...');
            const apiData = await financialInstitutionApiService.getInstitution(instId);
            console.log('✅ [Institution] Données reçues:', apiData?.denominationSociale);
            
            setInstitution(apiData as FinancialInstitution);
            setDataSource('network');
            lastLoadedIdRef.current = instId;
            setUsingMockData(false);
            
            // Sauvegarder en IndexedDB pour la prochaine fois
            if (userId && apiData) {
                await cacheService.set('institution', instId, apiData, userId, 'synced');
                console.log('💾 [Institution] Données sauvegardées dans IndexedDB');
            }
        } catch (err) {
            console.error('❌ Erreur chargement institution:', err);
            setError(err instanceof Error ? err.message : 'Une erreur est survenue.');
        } finally {
            setLoading(false);
            loadingIdRef.current = null;
        }
    }, [userId, loadFromIndexedDB, revalidateFromBackend]);

    // ✨ SIMPLIFICATION: Effet unique
    React.useEffect(() => {
        const effectiveId = institutionId || userFinancialInstitutionId;
        
        if (!effectiveId || !options.enabled) {
            setInstitution(null);
            setLoading(false);
            return;
        }
        
        if (!isValidUUID(effectiveId)) {
            console.log('⚠️ ID non-UUID ignoré:', effectiveId);
            setLoading(false);
            return;
        }
        
        // Ne pas recharger si déjà chargé
        if (lastLoadedIdRef.current === effectiveId && institution) {
            return;
        }
        
        loadInstitution(effectiveId);
    }, [institutionId, userFinancialInstitutionId, options.enabled, loadInstitution]);

    // Reset quand l'utilisateur change
    React.useEffect(() => {
        if (!userId) {
            lastLoadedIdRef.current = null;
            setInstitution(null);
        }
    }, [userId]);

    const createNewInstitution = async (institutionData: CreateFinancialInstitution) => {
        try {
            setIsUpdating(true);
            
            // ✨ IMPORTANT: Vérifier que l'utilisateur est connecté ET a un UUID valide
            // L'ID Auth0 (ex: google-oauth2|123) n'est pas valide pour le backend
            if (!user?.id) {
                throw new Error('Utilisateur non connecté. Veuillez vous reconnecter.');
            }
            
            if (!isValidUUID(user.id)) {
                console.error('❌ ID utilisateur non valide (pas un UUID):', user.id);
                throw new Error('Profil utilisateur incomplet. Veuillez rafraîchir la page et réessayer.');
            }
            
            // ✨ IMPORTANT: Création via API uniquement (pas de mock)
            // Comme pour Company, la création doit passer par l'API pour avoir un UUID valide
            
            console.log('📤 Création d\'une institution via l\'API...');
            console.log('👤 UserId (UUID validé):', user.id);
            
            // ✨ Ajouter le userId aux données (requis par le backend)
            const dataWithUserId = {
                ...institutionData,
                userId: user.id,
            };
            
            console.log('📦 Données envoyées:', dataWithUserId);
            
            const apiData = await financialInstitutionApiService.createInstitution(dataWithUserId);
            const newInstitution = apiData as FinancialInstitution;
            console.log('✅ Institution créée via l\'API avec succès, ID:', newInstitution.id);
            
            setInstitution(newInstitution);
            setDataSource('network');
            setUsingMockData(false);
            
            // ✨ Sauvegarder dans IndexedDB
            if (userId && newInstitution.id) {
                await cacheService.set('institution', newInstitution.id, newInstitution, userId, 'synced');
                console.log('💾 [Institution] Nouvelle institution sauvegardée dans IndexedDB');
            }
            
            // ✨ Lier l'institution à l'utilisateur
            try {
                console.log('🔗 Liaison de l\'institution à l\'utilisateur...');
                const updatedUser = await updateProfile({ financialInstitutionId: newInstitution.id });
                
                if (updatedUser && updatedUser.financialInstitutionId === newInstitution.id) {
                    console.log('✅ Institution liée à l\'utilisateur avec succès:', newInstitution.id);
                } else {
                    console.warn('⚠️ La liaison utilisateur-institution a échoué');
                }
            } catch (linkError) {
                console.error('❌ Erreur lors de la liaison utilisateur-institution:', linkError);
                showToast({ 
                    type: 'warning', 
                    title: 'Avertissement', 
                    message: 'Institution créée mais non liée à votre profil. Veuillez rafraîchir la page.' 
                });
            }
            
            showToast({ type: 'success', title: 'Succès', message: 'Institution financière créée avec succès !' });
            
            return newInstitution;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Une erreur est survenue.';
            setError(errorMessage);
            console.error('❌ Erreur lors de la création de l\'institution:', errorMessage);
            console.error('❌ Détails de l\'erreur:', err);
            showToast({ type: 'error', title: 'Erreur', message: `Erreur lors de la création de l'institution : ${errorMessage}` });
            return null;
        } finally {
            setIsUpdating(false);
        }
    };

    /**
     * ✨ Met à jour l'institution ou la crée si elle n'existe pas
     * Comportement identique à useCompany.updateCompany
     */
    const updateInstitutionData = async (updates: Partial<FinancialInstitution>) => {
        // ✨ CORRECTION: Utiliser l'ID effectif (props OU profil utilisateur)
        // Si pas d'ID effectif OU si l'ID n'est pas un UUID valide → créer une nouvelle institution
        if (!effectiveInstitutionId || !hasValidUUID) {
            console.log('📝 Pas d\'ID institution valide, création d\'une nouvelle institution...');
            console.log(`   effectiveInstitutionId: ${effectiveInstitutionId}, isValidUUID: ${hasValidUUID}`);
            return await createNewInstitution(updates as CreateFinancialInstitution);
        }

        try {
            setIsUpdating(true);
            
            // ✨ IMPORTANT: Mise à jour via API uniquement (pas de fallback mock)
            // Comme pour Company.updateCompany
            console.log(`🔄 Mise à jour de l'institution ${effectiveInstitutionId}...`);
            console.log('📤 Envoi des données à l\'API...');
            
            const apiData = await financialInstitutionApiService.updateInstitution(effectiveInstitutionId, updates);
            const updatedInstitution = apiData as FinancialInstitution;
            console.log('✅ Données mises à jour via l\'API avec succès');
            
            setInstitution(updatedInstitution);
            setDataSource('network');
            setUsingMockData(false);
            
            // ✨ Mettre à jour IndexedDB
            if (userId && updatedInstitution?.id) {
                await cacheService.set('institution', updatedInstitution.id, updatedInstitution, userId, 'synced');
                console.log('💾 [Institution] Données mises à jour dans IndexedDB');
            }
            
            showToast({ type: 'success', title: 'Succès', message: 'Informations mises à jour avec succès !' });
            
            return updatedInstitution;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Une erreur est survenue.';
            setError(errorMessage);
            console.error('❌ Erreur lors de la mise à jour de l\'institution:', errorMessage);
            showToast({ type: 'error', title: 'Erreur', message: `Erreur lors de la mise à jour : ${errorMessage}` });
            return null;
        } finally {
            setIsUpdating(false);
        }
    };

    const uploadLogoFile = async (logoFile: File) => {
        // ✨ CORRECTION: Utiliser l'ID effectif
        if (!effectiveInstitutionId || !hasValidUUID) {
            showToast({ type: 'error', title: 'Erreur', message: 'Veuillez d\'abord sauvegarder l\'institution avant d\'uploader un logo.' });
            return null;
        }

        try {
            setIsUpdating(true);
            
            // 1. Upload vers Cloudinary d'abord
            console.log('📤 Upload du logo vers Cloudinary...');
            const cloudinaryResult = await cloudinaryService.uploadLogo(logoFile, 'institution');
            console.log('✅ Logo uploadé sur Cloudinary:', cloudinaryResult.url);
            
            // 2. Envoyer l'URL au backend pour mise à jour
            await financialInstitutionApiService.updateInstitution(effectiveInstitutionId, {
                logo: cloudinaryResult.url
            });
            console.log('✅ URL du logo enregistrée dans le backend');
            
            // Mise à jour du state avec le nouvel URL du logo
            if (institution) {
                const updatedInstitution = { ...institution, logo: cloudinaryResult.url };
                setInstitution(updatedInstitution);
                
                // ✨ Mettre à jour IndexedDB
                if (userId) {
                    await cacheService.set('institution', effectiveInstitutionId, updatedInstitution, userId, 'synced');
                }
            }
            
            showToast({ type: 'success', title: 'Succès', message: 'Logo uploadé avec succès !' });
            return cloudinaryResult.url;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Une erreur est survenue.';
            setError(errorMessage);
            console.error('❌ Erreur lors de l\'upload du logo:', errorMessage);
            showToast({ type: 'error', title: 'Erreur', message: `Erreur lors de l'upload du logo : ${errorMessage}` });
            return null;
        } finally {
            setIsUpdating(false);
        }
    };

    // Fonction pour forcer le rafraîchissement des données
    const refreshInstitution = React.useCallback(() => {
        const effectiveId = institutionId || userFinancialInstitutionId;
        if (effectiveId && isValidUUID(effectiveId)) {
            lastLoadedIdRef.current = null;
            lastBackendFetchRef.current = 0;
            loadInstitution(effectiveId);
        }
    }, [institutionId, userFinancialInstitutionId, loadInstitution]);
    
    return {
        institution,
        loading,
        isUpdating,
        error,
        usingMockData,
        hasValidUUID, // ✨ Nouvel indicateur pour savoir si l'ID est un UUID valide
        // ✨ NOUVEAUX: États du cache IndexedDB
        dataSource,        // 'cache' | 'network' | 'none' - D'où viennent les données
        isRevalidating,    // true si revalidation backend en cours
        // Actions
        refreshInstitution,
        createNewInstitution,
        updateInstitutionData,
        uploadLogoFile,
    };
};
